import { supabase } from './supabase';
import {
  MASS_FLOW_TEMPLATE,
  buildFlowBlock,
  computeFlowPointMetrics,
  defaultFlowFamilyData,
  finiteOrNull,
  flowToCanonical,
  mergeFlowFamilyData,
  resolveFlowTemplate,
  suggestFlowSetpoints,
} from './flow-calibration-profile';

export { getTodayDailyEnvironmentLog, upsertTodayDailyEnvironmentLog } from './api-pressure-manometer-pilot';

export const DEFAULT_FLOW_VALIDITY_MONTHS = 12;
const VALIDITY_DAYS_BY_MONTHS = { 3: 90, 6: 181, 12: 365, 24: 730 };

function sanitizePatch(patch = {}) {
  return Object.fromEntries(Object.entries(patch).filter(([, value]) => (
    value !== undefined && !(typeof value === 'number' && !Number.isFinite(value))
  )));
}

async function currentUserId() {
  return (await supabase.auth.getUser())?.data?.user?.id ?? null;
}

export function resolveFlowValidityDays(months = DEFAULT_FLOW_VALIDITY_MONTHS) {
  const value = Number(months);
  if (!Number.isFinite(value) || value <= 0) return VALIDITY_DAYS_BY_MONTHS[DEFAULT_FLOW_VALIDITY_MONTHS];
  return VALIDITY_DAYS_BY_MONTHS[value] ?? Math.round((365 / 12) * value);
}

export function addFlowValidityDaysToIsoDate(isoDate, months = DEFAULT_FLOW_VALIDITY_MONTHS) {
  const base = String(isoDate || '').slice(0, 10);
  if (!base) return null;
  const date = new Date(`${base}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setDate(date.getDate() + resolveFlowValidityDays(months));
  return date.toISOString().slice(0, 10);
}

async function getTodayEnvironment() {
  const { getTodayDailyEnvironmentLog } = await import('./api-pressure-manometer-pilot');
  try {
    return await getTodayDailyEnvironmentLog();
  } catch {
    return null;
  }
}

export async function createFlowSession({
  instrumentId,
  serviceOrderId = null,
  serviceOrderCode = null,
  certificateType = 'NO_SAC',
  validityMonths = DEFAULT_FLOW_VALIDITY_MONTHS,
  templateCode = MASS_FLOW_TEMPLATE.code,
}) {
  if (!instrumentId) throw new Error('instrumentId es requerido');
  const template = resolveFlowTemplate(templateCode);
  const { data: instrument, error: instrumentError } = await supabase
    .from('instruments')
    .select(`id, tag, lab_id, brand, model, serial, type, unit, resolution, range_min, range_max, tolerance,
             client:client_id ( id, client_number, name, address, locality, province )`)
    .eq('id', instrumentId)
    .single();
  if (instrumentError) throw instrumentError;

  let normalizedResolution;
  let normalizedRangeMin;
  let normalizedRangeMax;
  try {
    normalizedResolution = flowToCanonical(instrument?.resolution, instrument?.unit || template.unit, template.code);
    normalizedRangeMin = flowToCanonical(instrument?.range_min, instrument?.unit || template.unit, template.code);
    normalizedRangeMax = flowToCanonical(instrument?.range_max, instrument?.unit || template.unit, template.code);
  } catch (error) {
    throw new Error(`${error.message} Configurá una unidad compatible antes de iniciar la calibración.`);
  }

  const dailyEnvironment = await getTodayEnvironment();
  const familyData = mergeFlowFamilyData({
    ...defaultFlowFamilyData(template.code),
    template_code: template.code,
    original_instrument_unit: instrument?.unit || null,
    values_normalized_to: template.unit,
  }, template.code);
  const payload = {
    family: 'flow',
    instrument_id: instrument.id,
    service_order_id: serviceOrderId,
    service_order_code: serviceOrderCode || null,
    certificate_type: certificateType,
    status: 'draft',
    template_code: template.code,
    template_revision: template.revision,
    client_number: instrument?.client?.client_number ? String(instrument.client.client_number) : null,
    client_name: instrument?.client?.name || null,
    client_address: instrument?.client?.address || null,
    client_city: instrument?.client?.locality || null,
    client_province: instrument?.client?.province || null,
    instrument_object: instrument?.type || template.objectFallback,
    instrument_brand: instrument?.brand || null,
    instrument_model: instrument?.model || null,
    instrument_serial: instrument?.serial || null,
    instrument_tag: instrument?.tag || null,
    instrument_unit: template.unit,
    instrument_resolution: normalizedResolution,
    instrument_range_min: normalizedRangeMin,
    instrument_range_max: normalizedRangeMax,
    instrument_class: finiteOrNull(instrument?.tolerance),
    standard_id: null,
    standard_name: null,
    standard_serial: null,
    standard_certificate: null,
    standard_calibration_date: null,
    ambient_pressure_avg: finiteOrNull(dailyEnvironment?.pressure_hpa),
    ambient_temp_avg: finiteOrNull(dailyEnvironment?.temp_c),
    ambient_humidity_avg: finiteOrNull(dailyEnvironment?.humidity_rh),
    validity_months: Number(validityMonths) || DEFAULT_FLOW_VALIDITY_MONTHS,
    family_data: familyData,
    created_by: await currentUserId(),
  };

  const { data: session, error } = await supabase.from('calibration_sessions').insert(payload).select('*').single();
  if (error) throw error;
  const { error: blockError } = await supabase.from('calibration_session_blocks').insert({
    ...buildFlowBlock(template.code),
    resolution: normalizedResolution,
    session_id: session.id,
  });
  if (blockError) throw blockError;
  return session;
}

export async function createMassFlowSession(options = {}) {
  return createFlowSession({ ...options, templateCode: options.templateCode || MASS_FLOW_TEMPLATE.code });
}

export async function listFlowStandardOptions({ templateCode = MASS_FLOW_TEMPLATE.code, onDate } = {}) {
  const { data, error } = await supabase.rpc('list_flow_standard_options_v2', {
    p_template_code: templateCode,
    p_on_date: onDate || new Date().toISOString().slice(0, 10),
  });
  if (error) throw error;
  return data || [];
}

export async function listMassFlowStandardOptions(options = {}) {
  return listFlowStandardOptions({ ...options, templateCode: MASS_FLOW_TEMPLATE.code });
}

export async function getFlowStandardConfiguration(sessionId) {
  const { data, error } = await supabase.rpc('get_flow_session_standard_configuration', { p_session_id: sessionId });
  if (error) throw error;
  return data || {};
}

export async function validateFlowStandardConfiguration(sessionId) {
  const { data, error } = await supabase.rpc('validate_flow_session_standard', { p_session_id: sessionId });
  if (error) throw error;
  return data || { valid: false, issues: ['No se pudo validar la trazabilidad.'], configuration: {} };
}

export async function validateFlowProfileData(sessionId) {
  const { data, error } = await supabase.rpc('validate_flow_profile_data', { p_session_id: sessionId });
  if (error) throw error;
  return data || { valid: false, issues: ['No se pudo validar el perfil de caudal.'] };
}

export async function configureFlowSessionStandard(sessionId, referenceCapabilityId) {
  const { data, error } = await supabase.rpc('configure_flow_session_standard', {
    p_session_id: sessionId,
    p_reference_capability_id: referenceCapabilityId || null,
  });
  if (error) throw error;
  return data;
}

export async function getFlowSession(sessionId) {
  const { data, error } = await supabase.from('calibration_sessions').select('*').eq('id', sessionId).eq('family', 'flow').single();
  if (error) throw error;
  return data;
}

export async function updateFlowSession(sessionId, patch) {
  const normalized = sanitizePatch(patch);
  if (normalized.family_data) {
    const { data: current } = await supabase.from('calibration_sessions').select('template_code').eq('id', sessionId).maybeSingle();
    normalized.family_data = mergeFlowFamilyData(normalized.family_data, current?.template_code);
  }
  const { data, error } = await supabase.from('calibration_sessions').update({
    ...normalized,
    updated_at: new Date().toISOString(),
    updated_by: await currentUserId(),
  }).eq('id', sessionId).eq('family', 'flow').select('*').single();
  if (error) throw error;
  return data;
}

export async function listFlowSessionsByInstrument(instrumentId, templateCode = null) {
  let query = supabase.from('calibration_sessions').select('*').eq('instrument_id', instrumentId).eq('family', 'flow');
  if (templateCode) query = query.eq('template_code', templateCode);
  const { data, error } = await query.order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function listFlowBlocks(sessionId) {
  const { data, error } = await supabase.from('calibration_session_blocks').select('*').eq('session_id', sessionId).order('sort_order');
  if (error) throw error;
  return data || [];
}

export async function listFlowPoints(sessionId) {
  const { data, error } = await supabase.from('calibration_session_points').select('*').eq('session_id', sessionId).order('point_no').order('series_no');
  if (error) throw error;
  return data || [];
}

export function flowPointsToRows(block, points = [], session = {}) {
  const template = resolveFlowTemplate(session.template_code);
  const pointCount = Number(block?.point_count) || template.pointCount;
  const suggested = suggestFlowSetpoints(session.instrument_range_min, session.instrument_range_max, template.code);
  const rows = Array.from({ length: pointCount }, (_, index) => {
    const row = { point_no: index + 1, setpoint: suggested[index] ?? '' };
    for (let repeat = 1; repeat <= template.repeatCount; repeat += 1) {
      row[`r${repeat}_pattern`] = '';
      row[`r${repeat}_instrument`] = '';
    }
    return row;
  });
  points.forEach((point) => {
    const row = rows[Number(point.point_no) - 1];
    if (!row || (block?.id && point.block_id !== block.id)) return;
    const repeat = Number(point.series_no);
    if (repeat < 1 || repeat > template.repeatCount) return;
    row.setpoint = point.setpoint ?? row.setpoint;
    row[`r${repeat}_pattern`] = point.pattern_reading ?? '';
    row[`r${repeat}_instrument`] = point.instrument_reading ?? '';
  });
  return rows;
}

export async function saveFlowPoints(sessionId, block, rows = [], templateCode = MASS_FLOW_TEMPLATE.code) {
  if (!sessionId || !block?.id) throw new Error('Sesión y bloque requeridos');
  const template = resolveFlowTemplate(templateCode);
  const payload = [];
  rows.forEach((row) => {
    const pointNo = Number(row.point_no);
    if (!Number.isFinite(pointNo)) return;
    const setpoint = finiteOrNull(row.setpoint);
    for (let repeat = 1; repeat <= template.repeatCount; repeat += 1) {
      const pattern = finiteOrNull(row[`r${repeat}_pattern`]);
      const instrument = finiteOrNull(row[`r${repeat}_instrument`]);
      if (pattern === null || instrument === null) continue;
      payload.push({
        session_id: sessionId, block_id: block.id, point_no: pointNo, series_no: repeat,
        direction: 'NA', setpoint: setpoint ?? pattern, pattern_reading: pattern,
        instrument_reading: instrument, unit: template.unit,
      });
    }
  });
  const { error: deleteError } = await supabase.from('calibration_session_points').delete().eq('session_id', sessionId).eq('block_id', block.id);
  if (deleteError) throw deleteError;
  if (!payload.length) return [];
  const { data, error } = await supabase.from('calibration_session_points').insert(payload).select('*');
  if (error) throw error;
  return data || [];
}

export async function saveMassFlowPoints(sessionId, block, rows = []) {
  return saveFlowPoints(sessionId, block, rows, MASS_FLOW_TEMPLATE.code);
}

export async function calculateFlowResults(sessionId) {
  const [validation, profileValidation] = await Promise.all([
    validateFlowStandardConfiguration(sessionId),
    validateFlowProfileData(sessionId),
  ]);
  if (!validation?.valid) throw new Error((validation?.issues || ['La configuración del patrón no es válida.']).join(' '));
  if (!profileValidation?.valid) throw new Error((profileValidation?.issues || ['La configuración del perfil no es válida.']).join(' '));
  const [session, blocks, points] = await Promise.all([getFlowSession(sessionId), listFlowBlocks(sessionId), listFlowPoints(sessionId)]);
  const template = resolveFlowTemplate(session.template_code);
  const effectiveSession = {
    ...session,
    family_data: mergeFlowFamilyData({
      ...session.family_data,
      standard_configuration: validation.configuration || {},
    }, template.code),
  };
  const block = blocks.find((item) => item.block_key === template.blockKey) || blocks[0];
  if (!block) throw new Error('La sesión no tiene un bloque de caudal.');
  const rows = flowPointsToRows(block, points, effectiveSession);
  const results = rows.map((row) => {
    const metrics = computeFlowPointMetrics(row, effectiveSession);
    if (metrics?.traceability_issue) throw new Error(`Punto ${row.point_no}: ${metrics.traceability_issue}`);
    return metrics ? { session_id: sessionId, block_id: block.id, point_no: row.point_no, ...metrics } : null;
  }).filter(Boolean);
  if (!results.length) throw new Error('Completá al menos un punto con lecturas patrón/instrumento.');
  const { error: deleteError } = await supabase.from('calibration_session_results').delete().eq('session_id', sessionId);
  if (deleteError) throw deleteError;
  const { error } = await supabase.from('calibration_session_results').insert(results);
  if (error) throw error;
  await updateFlowSession(sessionId, { status: 'calculated', family_data: effectiveSession.family_data });
  return results;
}

export async function calculateMassFlowResults(sessionId) {
  return calculateFlowResults(sessionId);
}

export async function listFlowResults(sessionId) {
  const { data, error } = await supabase.from('calibration_session_results').select('*').eq('session_id', sessionId).order('point_no');
  if (error) throw error;
  return data || [];
}
